import React, { useState, useEffect } from 'react';
import { useAuth } from '../auth/useAuth';
import apiService from '../services/api';
import TransactionTable from '../components/TransactionTable';

const Dashboard = () => {
  const { user, getAccessToken, signOut } = useAuth();
  const [profile, setProfile] = useState(null);
  const [transactions, setTransactions] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  useEffect(() => {
    loadDashboardData();
  }, []); // eslint-disable-line react-hooks/exhaustive-deps

  const loadDashboardData = async () => {
    try {
      setLoading(true);
      setError(null);
      
      const accessToken = await getAccessToken();
      if (!accessToken) {
        throw new Error('No access token available');
      }

      // Load profile and transactions in parallel
      const [profileData, transactionsData] = await Promise.all([
        apiService.getMe(accessToken),
        apiService.getTransactions(accessToken)
      ]);

      setProfile(profileData);
      setTransactions(transactionsData);
    } catch (err) {
      setError(err.message);
      console.error('Error loading dashboard data:', err);
    } finally {
      setLoading(false);
    }
  };

  const handleSignOut = async () => {
    try {
      await signOut();
    } catch (err) {
      console.error('Sign out error:', err);
    }
  };

  if (loading) {
    return (
      <div style={{ 
        display: 'flex', 
        justifyContent: 'center', 
        alignItems: 'center', 
        height: '100vh' 
      }}>
        <div>Loading dashboard...</div>
      </div>
    );
  }

  return (
    <div style={{ padding: '20px' }}>
      <header style={{ 
        display: 'flex', 
        justifyContent: 'space-between', 
        alignItems: 'center',
        marginBottom: '30px',
        paddingBottom: '20px',
        borderBottom: '1px solid #eee'
      }}>
        <h1>Provider Dashboard</h1>
        <div style={{ display: 'flex', alignItems: 'center', gap: '20px' }}>
          {profile && (
            <span>Welcome, {profile.name || profile.email || user.username}</span>
          )}
          <button
            onClick={handleSignOut}
            style={{
              padding: '8px 16px',
              backgroundColor: '#dc3545',
              color: 'white',
              border: 'none',
              borderRadius: '4px',
              cursor: 'pointer'
            }}
          >
            Sign Out
          </button>
        </div>
      </header>

      {error && (
        <div style={{ 
          color: 'red', 
          marginBottom: '20px',
          padding: '15px',
          backgroundColor: '#ffe6e6',
          borderRadius: '4px',
          border: '1px solid #ffcccc'
        }}>
          <strong>Error:</strong> {error}
          <button
            onClick={loadDashboardData}
            style={{
              marginLeft: '10px',
              padding: '5px 10px',
              backgroundColor: '#007bff',
              color: 'white',
              border: 'none',
              borderRadius: '3px',
              cursor: 'pointer'
            }}
          >
            Retry
          </button>
        </div>
      )}

      <div style={{ display: 'grid', gridTemplateColumns: '1fr 2fr', gap: '30px' }}>
        {/* Profile Section */}
        <div style={{ 
          padding: '20px',
          border: '1px solid #ddd',
          borderRadius: '8px',
          backgroundColor: '#f9f9f9'
        }}>
          <h2>Profile Information</h2>
          {profile ? (
            <div>
              <p><strong>Name:</strong> {profile.name || 'N/A'}</p>
              <p><strong>Email:</strong> {profile.email || 'N/A'}</p>
              <p><strong>Provider ID:</strong> {profile.id || 'N/A'}</p>
              <p><strong>Status:</strong> {profile.status || 'N/A'}</p>
              <p><strong>Created:</strong> {profile.createdAt ? new Date(profile.createdAt).toLocaleDateString() : 'N/A'}</p>
            </div>
          ) : (
            <p>No profile data available</p>
          )}
        </div>

        {/* Transactions Section */}
        <div style={{ 
          padding: '20px',
          border: '1px solid #ddd',
          borderRadius: '8px',
          backgroundColor: '#f9f9f9'
        }}>
          <h2>Recent Transactions</h2>
          {transactions && transactions.length > 0 ? (
            <TransactionTable transactions={transactions} />
          ) : (
            <p>No transactions found</p>
          )}
        </div>
      </div>
    </div>
  );
};

export default Dashboard;
